import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSearch,
  faFilter,
  faDownload,
  faChevronUp,
  faPen,
  faChevronDown,
  faFileAlt,
} from "@fortawesome/free-solid-svg-icons";
import { productionService } from "../services/production";
import { formatDateForDisplay } from "../utils/dateUtils";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

const Production = () => {
  const [productionOrders, setProductionOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Apenas "status" é de facto usado pelo backend (GET /orders) — searchTerm
  // nunca foi lido lá, por isso vive fora de `filters` e é filtrado localmente.
  const [filters, setFilters] = useState({
    status: "",
    parametersStatus: "", // "", "filled", "not_filled" (filtro local)
  });
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const isFirstSearchRun = useRef(true);
  const itemsPerPage = 10;
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));

  const navigate = useNavigate();

  useEffect(() => {
    const fetchProductionOrders = async () => {
      try {
        setLoading(true);
        const data = await productionService.getProductionOrders({ status: filters.status });
        // O backend já retorna responsibleName
        setProductionOrders(Array.isArray(data) ? data : []);
        setError("");
      } catch (err) {
        // Em caso de erro, definir productionOrders como vazio (não mostrar erro)
        console.error("Erro ao buscar ordens de produção:", err);
        setProductionOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProductionOrders();
  }, [filters.status]);

  const resetPage = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', '1');
      return newParams;
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    resetPage();
  };

  const handleSearchChange = (e) => {
    // Atualiza o campo imediatamente (UX responsiva); a busca/filtragem em si
    // só acontece depois do debounce (ver useEffect abaixo), evitando
    // consultas/refiltragens a cada tecla.
    setSearchInput(e.target.value);
  };

  // Só reseta a página quando o termo de busca (já com debounce) muda de
  // verdade — não na montagem inicial, para não perder a página da URL.
  useEffect(() => {
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false;
      return;
    }
    resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Filtro local: status de parâmetros + busca por nº/nome da ordem
  const filteredOrders = productionOrders.filter((order) => {
    if (filters.parametersStatus) {
      const items = order.production_order_items || [];
      if (items.length === 0) {
        if (filters.parametersStatus !== "not_filled") return false;
      } else if (filters.parametersStatus === "filled") {
        // Mostrar ordens onde TODOS os itens têm parâmetros preenchidos
        if (!items.every((item) => item.parameters_filled === true)) return false;
      } else if (filters.parametersStatus === "not_filled") {
        // Mostrar ordens onde ALGUM item não tem parâmetros preenchidos
        if (!items.some((item) => !item.parameters_filled)) return false;
      }
    }

    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      const items = order.production_order_items || [];
      const lots = order.production_lots || [];
      const matches =
        (order.order_number || "").toLowerCase().includes(term) ||
        (order.order_name || "").toLowerCase().includes(term) ||
        items.some((item) => (item.custom_lot_number || "").toLowerCase().includes(term)) ||
        lots.some((lot) => (lot.lot_number || "").toLowerCase().includes(term));
      if (!matches) return false;
    }

    return true;
  });

  // Calculate pagination
  const totalItems = filteredOrders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) handlePageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) handlePageChange(currentPage + 1);
  };

  const handleCreateOrder = () => {
    navigate("/new-production"); // Assumindo que a rota para criar nova ordem é /new-production
  };

  const handleViewDetails = (id) => {
    navigate(`/production-details/${id}`);
  };

  const productionEstadoLabels = {
    pending: "Pendente",
    in_progress: "Em Andamento",
    completed: "Concluído",
    cancelled: "Cancelado",
  };

  const productionEstadoColors = {
    pending: "bg-yellow-100 text-yellow-800",
    in_progress: "bg-blue-100 text-blue-800",
    completed: "bg-green-100 text-green-800",
    cancelled: "bg-red-100 text-red-800",
  };

  if (loading) {
    return (
      <div className="container-fluid p-4 sm:p-6 lg:p-8 text-center">
        A carregar ordens de produção...
      </div>
    );
  }

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      {/* Header responsivo */}
      <div className="mb-6">
        <h1 className="text-2xl mb-4">Produção</h1>
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            onClick={() => navigate("/production-drafts")}
            className="w-full sm:w-auto px-3 py-1.5 text-sm bg-white border border-neutral-300 text-neutral-900 rounded-lg hover:bg-neutral-50"
          >
            <FontAwesomeIcon icon={faFileAlt} className="mr-1.5" />
            Ver Rascunhos
          </button>
          <button
            onClick={handleCreateOrder}
            className="w-full sm:w-auto px-3 py-1.5 text-sm bg-neutral-900 text-white rounded-lg hover:bg-neutral-800"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1.5" />
            Nova Ordem
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow h-[calc(100vh-180px)]">
        <div className="p-4 sm:p-6 h-full flex flex-col">
          {/* Filtros responsivos */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              {/* Filtros principais */}
              <div className="flex flex-col sm:flex-row gap-3">
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full sm:w-auto px-3 py-2 border border-neutral-200 rounded-lg"
                >
                  <option value="">Todos os Estado</option>
                  {Object.keys(productionEstadoLabels).map((status) => (
                    <option key={status} value={status}>
                      {productionEstadoLabels[status]}
                    </option>
                  ))}
                </select>
                <select
                  name="parametersStatus"
                  value={filters.parametersStatus}
                  onChange={handleFilterChange}
                  className="w-full sm:w-auto px-3 py-2 border border-neutral-200 rounded-lg"
                >
                  <option value="">Todos os Parâmetros</option>
                  <option value="filled">Parâmetros Preenchidos</option>
                  <option value="not_filled">Parâmetros Não Preenchidos</option>
                </select>
                <div className="relative flex-1 sm:flex-initial">
                  <input
                    type="text"
                    placeholder="Buscar lote..."
                    value={searchInput}
                    onChange={handleSearchChange}
                    className="w-full sm:w-64 pl-10 pr-4 py-2 border border-neutral-200 rounded-lg"
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-3 text-neutral-400"
                  />
                </div>
              </div>
              
              {/* Botão de download */}
              <div className="flex justify-end sm:justify-start">
                <button className="p-2 hover:bg-neutral-100 rounded-lg">
                  <FontAwesomeIcon
                    icon={faDownload}
                    className="text-neutral-600"
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50 sticky top-0">
                  <tr>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm bg-neutral-50 sticky left-0 z-10 sm:relative sm:bg-transparent">Nº Ordem</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm">Produções</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm">Quantidade</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm">Estado</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm">Responsável</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {paginatedOrders.length > 0 ? (
                    paginatedOrders.map((order) => (
                      <React.Fragment key={order.id}>
                        <tr>
                          <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm bg-white sticky left-0 z-10 sm:relative sm:bg-transparent border-r border-neutral-200 sm:border-r-0">{order.order_number}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm">
                            <div className="flex flex-col">
                              <span className="text-xs text-neutral-500">
                                {order.start_date ? formatDateForDisplay(order.start_date) : ""}
                              </span>
                              <span className="font-medium">{order.order_name}</span>
                              <span className="text-xs text-neutral-400">#{order.order_number}</span>
                            </div>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm">
                            {order.total_quantity_to_produce} kg
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm">
                            <span
                              className={`px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm ${
                                productionEstadoColors[order.status] ||
                                "bg-neutral-100 text-neutral-800"
                              }`}
                            >
                              {productionEstadoLabels[order.status]}
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm">
                            <div className="flex items-center">
                              <img
                                src={`https://api.dicebear.com/7.x/notionists/svg?scale=200&seed=${order.responsible_user_id}`}
                                className="w-4 sm:w-6 h-4 sm:h-6 rounded-full mr-1 sm:mr-2"
                                alt="User Avatar"
                              />
                              <span className="text-xs sm:text-sm">{order.responsibleName}</span>
                            </div>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm">
                            <div className="flex space-x-1 sm:space-x-2">
                              <button
                                onClick={() => handleViewDetails(order.id)}
                                className="text-neutral-600 hover:text-neutral-900"
                              >
                                <FontAwesomeIcon icon={faPen} className="text-xs sm:text-sm" />
                              </button>
                              {/* Botão para expandir/colapsar detalhes, se necessário */}
                            </div>
                          </td>
                        </tr>
                        {/* Linha de detalhes colapsável, se houver */}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-2 sm:px-4 py-8 sm:py-12">
                        <div className="text-center">
                          <div className="text-neutral-500 mb-6">
                            <p className="text-lg mb-2">Nenhuma ordem de produção encontrada</p>
                            <p className="text-sm">Crie uma nova ordem para começar</p>
                          </div>
                          <button
                            onClick={handleCreateOrder}
                            className="px-6 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800"
                          >
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            Nova Ordem
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Paginação responsiva */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-6 gap-3">
              <div className="text-sm text-neutral-600 text-center sm:text-left">
                Mostrando {startIndex + 1} a {Math.min(endIndex, totalItems)} de {totalItems} ordens
              </div>
              <div className="flex justify-center sm:justify-end">
                <div className="flex space-x-1 sm:space-x-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="px-2 sm:px-3 py-1 sm:py-2 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>

                  {[...Array(Math.min(3, totalPages))].map((_, index) => {
                    let pageNumber;
                    if (totalPages <= 3) {
                      pageNumber = index + 1;
                    } else if (currentPage <= 2) {
                      pageNumber = index + 1;
                    } else if (currentPage >= totalPages - 1) {
                      pageNumber = totalPages - 2 + index;
                    } else {
                      pageNumber = currentPage - 1 + index;
                    }

                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-2 sm:px-3 py-1 sm:py-2 text-sm rounded-lg ${
                          currentPage === pageNumber
                            ? 'bg-neutral-900 text-white'
                            : 'border border-neutral-200 hover:bg-neutral-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="px-2 sm:px-3 py-1 sm:py-2 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Seguinte
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Production;
